import { useEffect, useState } from 'react';

const ENDPOINT = 'https://tsangamusic.goatcounter.com/counter/TOTAL.json';
const POLL_MS = 5 * 60 * 1000; // 5 min — matches Goatcounter cache window

// The endpoint 403s unless "allow using the visitor counter" is enabled in the
// Goatcounter site settings. Failing silently in production is deliberate (the nav
// just keeps showing ------), but a silent failure with no signal anywhere is how
// this counter stayed broken unnoticed — so warn once in development.
let warned = false;
const warnOnce = (reason) => {
  if (warned || process.env.NODE_ENV === 'production') return;
  warned = true;
  console.warn(
    `[useVisitorCount] visitor counter unavailable (${reason}). ` +
      'If this is a 403, enable "allow using the visitor counter" in the Goatcounter site settings.'
  );
};

/**
 * Live visitor counter pulled from the public Goatcounter endpoint.
 * Returns the total page-view count as a number, or null while loading
 * or if the fetch fails (caller decides how to render fallback).
 */
export function useVisitorCount() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const res = await fetch(ENDPOINT, { method: 'GET', mode: 'cors' });
        if (!res.ok) {
          warnOnce(`HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        const n = parseInt(data?.count, 10);
        if (!cancelled && Number.isFinite(n)) setCount(n);
      } catch (err) {
        // Network/CORS failure — keep last good value or stay null.
        warnOnce(err?.message || 'network error');
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return count;
}
